// src/cockpit4/components/CustomerCommunity.tsx
// ============================================
// 客户社群运营（客户中心 P2-功能10）- v2.0 详细改进计划
// 社群自动运营 / 客户反馈收集分析 / NPS 调研 / 口碑激励 / 社区知识库
// 数据来源：localStorage（xeaos_customer_community）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Users, MessageSquare, Heart, Star, BookOpen, Send } from "lucide-react";

interface CommunityGroup {
  id: string;
  name: string;
  members: number;
  activeToday: number;
  health: number;
  nps: number;
}

const INITIAL_GROUPS: CommunityGroup[] = [
  { id: "g1", name: "企业 AI 落地交流群", members: 386, activeToday: 45, health: 92, nps: 58 },
  { id: "g2", name: "酒店行业客户群", members: 158, activeToday: 18, health: 78, nps: 42 },
  { id: "g3", name: "XEAOS 超级用户群", members: 64, activeToday: 22, health: 96, nps: 72 },
];

const FEEDBACKS = [
  { id: "f1", user: "陈总", text: "知识库问答很实用，但希望支持更多格式导入", type: "建议", date: "今天" },
  { id: "f2", user: "李女士", text: "CRM 跟进提醒很及时，成交效率提升明显", type: "好评", date: "今天" },
  { id: "f3", user: "周老板", text: "工作流节点编辑希望支持撤销操作", type: "建议", date: "昨天" },
  { id: "f4", user: "赵经理", text: "营销日历和媒体工作室联动很方便！", type: "好评", date: "昨天" },
];

const KEY = "xeaos_customer_community";

function load(): CommunityGroup[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as CommunityGroup[];
  } catch { /* ignore */ }
  return INITIAL_GROUPS;
}

export default function CustomerCommunity() {
  const [groups, setGroups] = useState<CommunityGroup[]>(load);
  const [draft, setDraft] = useState("");
  const [sent, setSent] = useState(false);

  const stats = useMemo(() => {
    const totalMembers = groups.reduce((a, g) => a + g.members, 0);
    const avgNps = groups.reduce((a, g) => a + g.nps, 0) / groups.length;
    const positive = FEEDBACKS.filter((f) => f.type === "好评").length;
    return { totalMembers, avgNps, positive };
  }, [groups]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Users size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">客户社群运营</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Community</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="社群总成员" value={`${stats.totalMembers} 人`} color="#38BDF8" />
        <MetricCard label="平均 NPS" value={`${stats.avgNps.toFixed(0)}`} color="#A78BFA" />
        <MetricCard label="好评率" value={`${Math.round((stats.positive / FEEDBACKS.length) * 100)}%`} color="#34D399" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">客户社群 · 健康度</span>
        </div>
        <div className="space-y-1.5">
          {groups.map((g) => (
            <div key={g.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{g.name}</span>
                <span className="ml-auto text-[9px] font-mono text-gray-500">{g.members} 人 · 今日活跃 {g.activeToday}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${g.health}%`, background: g.health >= 85 ? "#34D399" : "#FBBF24" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-400">健康度 {g.health}</span>
                <span className="text-[9px] font-mono text-purple-400">NPS {g.nps}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Heart size={11} className="text-pink-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">客户反馈 · 自动收集分析</span>
          </div>
          <div className="space-y-1.5">
            {FEEDBACKS.map((f) => (
              <div key={f.id} className="flex items-start gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2">
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded mt-0.5 ${f.type === "好评" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{f.type}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-gray-300">{f.text}</div>
                  <div className="text-[8px] font-mono text-gray-600 mt-0.5">{f.user} · {f.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Send size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">社群促活 · 一键发布</span>
          </div>
          <textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setSent(false); }}
            placeholder="输入社群活动/话题内容，AI 自动适配各群风格..."
            className="w-full h-24 rounded-md border border-white/[0.06] bg-black/20 px-2.5 py-2 text-[10px] font-mono text-gray-300 placeholder-gray-600 outline-none focus:border-emerald-500/40 resize-none"
          />
          <button
            onClick={() => { setSent(true); }}
            className="mt-2 flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-[10px] font-mono text-emerald-300 hover:bg-emerald-500/20"
          >
            <Send size={10} /> 发布到全部社群
          </button>
          {sent && <div className="text-[9px] font-mono text-emerald-400 mt-1.5">✓ 已发布 · 3 个社群 · 预计触达 {groups.reduce((a, g) => a + g.members, 0)} 人</div>}
          <div className="flex items-center gap-1.5 mt-3 text-[9px] font-mono text-gray-500">
            <BookOpen size={10} /> 社区知识库：已沉淀 128 条最佳实践，AI 自动分类归档
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

